import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import RefinementSection from '../components/RefinementSection';
import EmotionCheckpoint from '../components/EmotionCheckpoint';

// ============ REVELATION EXPERIENCE ============
// This is NOT a results page. This is a mirror.

// Loading messages for intentional wait experience
const LOADING_STAGES = [
  { text: "Nous analysons tes réponses...", duration: 4000 },
  { text: "Nous comprenons ta vision...", duration: 6000 },
  { text: "Nous construisons ton identité...", duration: 8000 },
  { text: "Presque prête...", duration: null } // Final stage until API returns
];

const LoadingExperience = ({ loadingStage }) => {
  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center">
      <div className="text-center max-w-md px-6">
        {/* Pulsing circle */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative w-24 h-24 mx-auto mb-12"
        >
          {/* Outer ring - slow pulse */}
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 border border-[#D4AF37]/20 rounded-full"
          />
          {/* Middle ring */}
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.2, 0.5] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            className="absolute inset-2 border border-[#D4AF37]/30 rounded-full"
          />
          {/* Inner circle with icon */}
          <div className="absolute inset-4 border border-[#D4AF37]/40 rounded-full flex items-center justify-center bg-[#D4AF37]/5">
            <Sparkles className="text-[#D4AF37]" size={24} />
          </div>
        </motion.div>
        
        {/* Progress indicator */}
        <div className="flex justify-center gap-2 mb-8">
          {LOADING_STAGES.map((_, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0.3 }}
              animate={{ opacity: index <= loadingStage ? 1 : 0.3 }}
              className={`w-8 h-0.5 rounded-full transition-all duration-500 ${
                index <= loadingStage ? 'bg-[#D4AF37]' : 'bg-[#27272A]'
              }`}
            />
          ))}
        </div>
        
        {/* Animated text */}
        <AnimatePresence mode="wait">
          <motion.p
            key={loadingStage}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="text-white text-lg mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {LOADING_STAGES[loadingStage]?.text}
          </motion.p>
        </AnimatePresence>
        
        {/* Subtle subtext */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-[#52525B] text-xs tracking-wide"
        >
          Ce moment est pour toi
        </motion.p>
      </div>
    </div>
  );
};

const VisionRevelation = () => {
  const [stage, setStage] = useState(0); // 0: loading, 1: intro, 2-5: reveals, 6: final
  const [visionData, setVisionData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [loadingStage, setLoadingStage] = useState(0);
  const [error, setError] = useState(null);
  const [isRefining, setIsRefining] = useState(false);
  const [refinedVision, setRefinedVision] = useState(null);
  const [showRefinementMode, setShowRefinementMode] = useState(false);
  const [showEmotionCheckpoint, setShowEmotionCheckpoint] = useState(false);
  
  const { token, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Determine if already completed (after auth loads) - but allow refinement mode
  const isAlreadyCompleted = !authLoading && user?.vision_revelation_completed === true && !showRefinementMode;

  // Progressive loading stages (only if not already completed)
  useEffect(() => {
    if (!isGenerating || isAlreadyCompleted || authLoading) return;
    
    const timers = [];
    let currentTime = 0;
    
    LOADING_STAGES.forEach((stage, index) => {
      if (stage.duration && index < LOADING_STAGES.length - 1) {
        currentTime += stage.duration;
        const timer = setTimeout(() => {
          setLoadingStage(index + 1);
        }, currentTime);
        timers.push(timer);
      }
    });
    
    return () => timers.forEach(t => clearTimeout(t));
  }, [isGenerating, isAlreadyCompleted]);

  // Generate Vision Identity on mount (only if not already completed)
  useEffect(() => {
    if (isAlreadyCompleted || authLoading) return;
    
    const generateVision = async () => {
      try {
        const response = await axios.post(
          `${API}/vision-identity/generate`,
          { brand_name: 'Personal Brand' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setVisionData(response.data);
        setIsGenerating(false);
        // Small delay before showing intro
        setTimeout(() => setStage(1), 800);
      } catch (err) {
        console.error('Vision generation error:', err);
        setError('Une erreur est survenue. Réessaie.');
        setIsGenerating(false);
      }
    };

    generateVision();
  }, [token]);

  const nextStage = () => {
    setStage(prev => prev + 1);
  };

  const goToEmotionCheckpoint = () => {
    setShowEmotionCheckpoint(true);
  };

  const goToContentRevelation = () => {
    navigate('/content-revelation');
  };

  const goToRefinement = () => {
    setShowRefinementMode(true); // Enable refinement mode
  };
  
  // Refinement handler
  const handleRefine = async (notes) => {
    setIsRefining(true);
    try {
      const response = await axios.post(
        `${API}/vision-identity/refine`,
        { refinement_notes: notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefinedVision(response.data);
      setVisionData(response.data); // Update main vision with refined version
    } catch (err) {
      console.error('Vision refinement error:', err);
    }
    setIsRefining(false);
  };

  const continueJourney = () => {
    // Navigate to the next incomplete step
    if (!user?.content_revelation_completed) {
      navigate('/content-revelation');
    } else if (!user?.first_action_completed) {
      navigate('/first-action');
    } else {
      navigate('/dashboard');
    }
  };

  // Continue journey but show emotion checkpoint first
  const continueWithCheckpoint = () => {
    setShowEmotionCheckpoint(true);
  };

  // Show loading while auth is loading
  if (authLoading) return <LoadingExperience loadingStage={0} />;
  
  // Show standalone refinement screen when user clicked "Affiner ma vision"
  if (showRefinementMode) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <AnimatePresence mode="wait">
          <motion.div
            key="standalone-refinement"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <RefinementSection
              stepName="vision"
              isRefining={isRefining}
              refinedData={refinedVision}
              onRefine={handleRefine}
              onContinue={goToContentRevelation}
              onSkip={() => setShowRefinementMode(false)}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }
  
  // Show "already completed" screen if step is done
  if (isAlreadyCompleted) {
    // Show Emotion Checkpoint if triggered
    if (showEmotionCheckpoint) {
      return (
        <EmotionCheckpoint
          step="vision"
          onComplete={continueJourney}
        />
      );
    }
    
    return (
      <div className="min-h-screen bg-[#030303]">
        <AnimatePresence mode="wait">
          <motion.div
            key="already-completed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="w-20 h-20 mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-full flex items-center justify-center"
            >
              <Sparkles className="text-[#D4AF37]" size={32} />
            </motion.div>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mb-6"
            >
              Étape complétée
            </motion.p>
            
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-white text-2xl md:text-4xl text-center mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ta vision a déjà été révélée.
            </motion.h2>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-[#71717A] text-base text-center max-w-md mb-12"
            >
              Tu as déjà découvert ton identité de vision.
              <br />
              Que souhaites-tu faire maintenant ?
            </motion.p>
            
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="flex flex-col items-center gap-4"
            >
              <Button
                onClick={continueWithCheckpoint}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-10 py-6 text-base font-medium"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="continue-journey"
              >
                Continuer mon parcours <ArrowRight className="ml-3" size={18} />
              </Button>
              
              <Button
                onClick={goToRefinement}
                className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-5"
                data-testid="refine-vision"
              >
                <Sparkles className="mr-2" size={16} />
                Affiner ma vision
              </Button>
              
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-transparent text-[#71717A] hover:text-white px-8 py-3"
                data-testid="go-dashboard"
              >
                Accéder à mon espace
              </Button>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // Loading experience with progressive stages
  if (isGenerating) {
    return <LoadingExperience loadingStage={loadingStage} />;
  }

  // Retry vision generation
  const retryGeneration = () => {
    setError(null);
    setIsGenerating(true);
    setLoadingStage(0);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 mx-auto mb-6 border border-red-400/30 rounded-full flex items-center justify-center">
            <Sparkles className="text-red-400" size={24} />
          </div>
          <p className="text-white text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Oups, une erreur s'est produite
          </p>
          <p className="text-[#A1A1AA] text-sm mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <Button 
              onClick={retryGeneration} 
              className="bg-[#D4AF37] hover:bg-[#B8860B] text-black w-full"
              data-testid="vision-retry-btn"
            >
              Réessayer <ArrowRight className="ml-2" size={16} />
            </Button>
            <button 
              onClick={() => navigate('/profile-revelation')}
              className="text-[#71717A] text-sm hover:text-white transition-colors"
            >
              Retourner à l'étape précédente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] overflow-hidden">
      <AnimatePresence mode="wait">
        
        {/* ============ STAGE 1: INTRO ============ */}
        {stage === 1 && (
          <motion.div
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="w-20 h-20 mb-12 border border-[#D4AF37]/30 rounded-full flex items-center justify-center"
            >
              <Sparkles className="text-[#D4AF37]" size={32} />
            </motion.div>
            
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-6"
            >
              Ta révélation
            </motion.p>
            
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="text-white text-3xl md:text-5xl text-center max-w-2xl leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Voici ce que nous voyons en toi.
            </motion.h1>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="mt-16"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-10 py-6 text-base tracking-wide"
                data-testid="revelation-continue"
              >
                Découvrir <ArrowRight className="ml-3" size={18} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 2: WHO SHE IS BECOMING ============ */}
        {stage === 2 && (
          <motion.div
            key="becoming"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.p
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-8"
            >
              Qui tu deviens
            </motion.p>
            
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 1 }}
              className="max-w-3xl text-center"
            >
              <p 
                className="text-white text-xl md:text-2xl leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {visionData?.who_she_is_becoming}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2, duration: 0.8 }}
              className="mt-16"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/30 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-white px-8 py-5"
                data-testid="revelation-next-2"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 3: POSITIONING DIRECTION ============ */}
        {stage === 3 && (
          <motion.div
            key="positioning"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.p
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-8"
            >
              Ta direction
            </motion.p>
            
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 1 }}
              className="max-w-3xl text-center"
            >
              <p 
                className="text-white text-xl md:text-2xl leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {visionData?.positioning_direction}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2, duration: 0.8 }}
              className="mt-16"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/30 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-white px-8 py-5"
                data-testid="revelation-next-3"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 4: ALIGNMENT INSIGHTS (Reframed blocks) ============ */}
        {stage === 4 && (
          <motion.div
            key="insights"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.p
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-12"
            >
              Ce que tu dois savoir
            </motion.p>
            
            <div className="max-w-2xl space-y-8">
              {visionData?.alignment_insights?.map((insight, index) => (
                <motion.div
                  key={index}
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5 + (index * 0.5), duration: 0.8 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-px h-full min-h-[60px] bg-gradient-to-b from-[#D4AF37] to-transparent flex-shrink-0" />
                  <p 
                    className="text-[#E5E5E5] text-base md:text-lg leading-relaxed"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {insight}
                  </p>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.5, duration: 0.8 }}
              className="mt-16"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/30 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-white px-8 py-5"
                data-testid="revelation-next-4"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 5: SIGNATURE STATEMENT (Strong moment) ============ */}
        {stage === 5 && (
          <motion.div
            key="signature"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.p
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-4"
            >
              Ta signature
            </motion.p>
            
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.8, duration: 1.2, ease: "easeOut" }}
              className="max-w-4xl text-center py-12"
            >
              {/* Decorative line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1.2, duration: 0.8 }}
                className="w-24 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mb-10"
              />
              
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.5, duration: 1 }}
                className="text-[#D4AF37] text-2xl md:text-4xl leading-relaxed italic"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                "{visionData?.signature_statement}"
              </motion.p>
              
              {/* Decorative line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 2, duration: 0.8 }}
                className="w-24 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mt-10"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.8, duration: 0.8 }}
              className="mt-12"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/30 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-white px-8 py-5"
                data-testid="revelation-next-5"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 6: FINAL - ENTER VISION LAB ============ */}
        {stage === 6 && (
          <motion.div
            key="final"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="w-24 h-24 mb-10 border border-[#D4AF37]/40 rounded-full flex items-center justify-center"
            >
              <Sparkles className="text-[#D4AF37]" size={40} />
            </motion.div>
            
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="text-white text-3xl md:text-5xl text-center mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Tu sais maintenant.
            </motion.h2>
            
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="text-[#A1A1AA] text-lg text-center max-w-xl mb-8"
            >
              Cette clarté est le premier pas.
            </motion.p>
            
            {/* Refinement Section - Co-creation feature */}
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.5 }}
              className="w-full"
            >
              <RefinementSection
                stepName="vision"
                isRefining={isRefining}
                refinedData={refinedVision}
                onRefine={handleRefine}
                onContinue={goToEmotionCheckpoint}
                onSkip={goToEmotionCheckpoint}
              />
            </motion.div>
          </motion.div>
        )}

        {/* Emotion Checkpoint */}
        {showEmotionCheckpoint && (
          <EmotionCheckpoint
            step="vision"
            onComplete={goToContentRevelation}
          />
        )}
        
      </AnimatePresence>
    </div>
  );
};

export default VisionRevelation;
